import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Button,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Input,
    InputLabel,
    Paper,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Alert } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import axios from "axios";
import * as React from "react";
import nextId from "react-id-generator";
import { Config } from "../../../config/config";

import { getGlobalVar, Helpers } from "../../../utils/helpers";
import { PopupMessage } from "../../../utils/message";

interface MessagesWrappedState {
    rewards: Array<{
        key: string;
        item: string | number;
        amount: number;
    }>;
    images: Array<{
        name: string;
        url: string;
        key: string;
    }>;
    vipPlayers: null | number;
}

interface MessagesWrappedProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
    form: any;
    createTask(data): void;
}

export class TaskMessage extends React.Component<MessagesWrappedProps, MessagesWrappedState> {
    sending = false;
    rewardItems: any;

    constructor(props) {
        super(props);

        this.state = {
            rewards: [],
            images: [],
            vipPlayers: null,
        };

        this.rewardItems = getGlobalVar("ITEMS").map(item => {
            const name = Helpers.getLocalizationName(item.name);

            return {
                ...item,
                name,
                firstLetter: item.type.replace("_", " ").toUpperCase(),
            };
        });
    }

    componentDidMount() {
        this.fetchData();
    }

    async fetchData() {
        try {
            const response = await axios.get(
                `${Config.getInstance.apiUrl}/api/users/active_vip`,
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                    },
                },
            );

            this.setState({
                vipPlayers: response.data,
            });
        } catch (e) {
            PopupMessage.error("Failed to load VIP user amount: " + e.message);
        }
    }

    addNewItem = () => {
        this.setState({
            rewards: [...this.state.rewards, {
                key: nextId(),
                item: "",
                amount: 0,
            }],
        });
    };

    uploadFile = async (e) => {
        const data = new FormData();
        data.append("image", e.target.files[0]);
        try {
            const response = await axios.post(`${Config.getInstance.apiUrl}/api/messages/attachment`, data, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    "Content-Type": "multipart/form-data",
                },
            });
            this.addImage(
                response.data.data.name,
                response.data.data.url,
                response.data.data.key,
            );
        } catch (e) {
            PopupMessage.error("Failed to upload image! Error: " + e.message);
        }
    }

    async deleteFile(key: string) {
        try {
            await axios.delete(`${Config.getInstance.apiUrl}/api/messages/attachment/${key}`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            });
            this.removeImage(key);
        } catch (e) {
            PopupMessage.error("Failed to delete image! Error: " + e.response.data.data);
        }
    }

    removeImage(key: string) {
        const images = [...this.state.images];
        const index = images.findIndex((image) => image.key === key);
        if (index >= 0) {
            images.splice(index, 1);
            this.setState({
                images,
            })
        }
    }

    addImage = (name: string, url: string, key: string) => {
        this.setState({
            images: [...this.state.images, {
                name,
                url,
                key,
            }],
        });
    };

    removeItem = (itemId) => {
        this.setState(prevState => ({
            rewards: prevState.rewards.filter(reward => reward.key !== itemId)
        }));
    };

    changeRewardItemAmount = (id: string, e: any) => {
        const tempVal = this.state.rewards;
        const element = tempVal.findIndex(reward => reward.key === id);

        tempVal[element].amount = +e.target.value;

        this.setState({rewards: tempVal});
    };

    changeRewardItem = (id: string, value: string) => {
        const tempVal = this.state.rewards;
        const element = tempVal.findIndex(reward => reward.key === id);

        tempVal[element].item = value;

        this.setState({rewards: tempVal});
    };

    createMessageTask() {
        this.props.createTask({
            rewards: this.state.rewards.reduce((list, item) => {
                if (!!(item.item as any).id && !!item.amount) {
                    list.push({
                        item: (item.item as any).id,
                        amount: item.amount
                    })
                }

                return list;
            }, []),
            title: this.props.form.getFieldValue("title"),
            message: this.props.form.getFieldValue("body"),
            receiverIds: this.props.form.getFieldValue("receiverIds")?.split(",")?.map(id => id.trim())?.filter(id => !!id) || [],
            type: this.props.form.getFieldValue("receiver_type"),
            receiverStore: this.props.form.getFieldValue("receiver_store"),
            images: this.state.images,
        });
    };

    renderMessageRewards = () => {
        if ((this.state.rewards || []).length === 0) {
            return <Typography variant="body1" gutterBottom>
                No items selected
            </Typography>;
        }

        return (this.state.rewards || []).map(reward => (
            <div style={{display: "inline-block", width: "100%", marginBottom: 10}} key={reward.key}>
                <FormControl style={{
                    marginRight: 8,
                    width: "70%",
                    marginBottom: 10,
                }}>
                    <Autocomplete
                        onChange={(_, newValue) => this.changeRewardItem(reward.key, newValue)}
                        value={reward.item}
                        options={this.rewardItems.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))}
                        groupBy={option => option.firstLetter}
                        getOptionLabel={option => option.name}
                        renderInput={(params) => <TextField {...params} label="Reward" id={`reward_select_${reward.key}`}/>}
                        size="small"
                    />
                </FormControl>

                <FormControl style={{
                    width: "20%",
                    marginBottom: 10,
                }}>
                    <TextField
                        id={`reward-title-input-${reward.key}`}
                        type="number"
                        value={reward.amount}
                        onChange={event => this.changeRewardItemAmount(reward.key, event)}
                        size="small"
                        label="Amount"
                    />
                </FormControl>

                {this.state.rewards.length >= 1 &&
                <IconButton
                    aria-label="Delete"
                    style={{
                        marginLeft: 10
                    }}
                    onClick={() => this.removeItem(reward.key)}
                    size="medium"
                >
                    <DeleteIcon fontSize="inherit"/>
                </IconButton>
                }
            </div>
        ));
    }

    renderImages() {
        return this.state.images.map(image => <TableRow>
            <TableCell>
                <Button disableElevation
                    name={image.key}
                    onClick={() => this.deleteFile(image.key)}
                    style={{position: "relative"}}
                    variant="contained"
                    color="secondary"
                >
                    Delete
                </Button>
            </TableCell>
            <TableCell>
                <img height="100px"
                     src={image.url}
                />
            </TableCell>
        </TableRow>)
    }

    renderReceiverWarning() {
        if (this.props.form.getFieldValue("receiver_type") === "global") {
            return <Alert severity="warning">
                All players will receive this message.
                The message will show up to them on their next play session
            </Alert>;
        } else if (this.props.form.getFieldValue("receiver_type") === "users") {
            return <Alert severity="warning">
                Only users in the list will receive this message. You can provide a comma separated list of user IDs or
                Tags. Try to keep the player count below 1000. If you need more, consult a developer
            </Alert>;
        } else if (this.props.form.getFieldValue("receiver_type") === "squads") {
            return <Alert severity="warning">
                Only users in the provided alliances will receive this message.
                You can provide a comma separated list of squad IDs. Try to keep the alliance count below 100. If you
                need more, consult a developer
            </Alert>;
        } else if (this.props.form.getFieldValue("receiver_type") === "vip_users") {
            return <Alert severity="warning">
                Only users in the list will receive this message. Try to keep the player count below 2000. If you need more, consult a developer.
            </Alert>;
        }

        return null;
    }

    renderReceiverInput() {
        const {getFieldDecorator, getFieldError} = this.props.form;

        if (this.props.form.getFieldValue("receiver_type") === "global") {
            return null;
        }

        return <FormControl
            fullWidth={true}
            required={true}
            style={{marginBottom: 30, marginTop: 20}}
            error={getFieldError("receiverIds")}
        >
            {getFieldDecorator("receiverIds",
                {
                    validateTrigger: "onBlur",
                    rules: [{
                        required: true,
                        message: "Please enter the IDs",
                    }]
                },
            )(
                <TextField
                    id="message-receiver-input"
                    size="small"
                    label="Receiver List"
                />
            )}

            {getFieldError("receiverIds") ?
                <FormHelperText>{getFieldError("receiverIds").join(",")}</FormHelperText>
                : null}
        </FormControl>;
    }

    renderStoreInput() {
        if (this.props.form.getFieldValue("receiver_type") !== "global") {
            return null;
        }

        return <FormControl
            fullWidth={true}
            required={true}
            style={{marginBottom: 30, marginTop: 20}}
            className="with-select"
        >
            <InputLabel id="messages-store-label">Store</InputLabel>

            {this.props.form.getFieldDecorator("receiver_store",
                    {
                        rules: [{
                            required: true,
                            message: "Please select receiver type",
                        }]
                    },
            )(
                <Select
                    labelId="messages-store-label"
                    id="messagesReceivers"
                    size="small"
                    label="Store"
                >
                    <MenuItem value={"all"}>All</MenuItem>
                    <MenuItem value={"google"}>Google</MenuItem>
                    <MenuItem value={"apple"}>Apple</MenuItem>
                    <MenuItem value={"samsung"}>Samsung</MenuItem>
                    <MenuItem value={"huawei"}>Huawei</MenuItem>
                </Select>

            )}
        </FormControl>;
    }

    render() {
        const {classes} = this.props;
        const {getFieldDecorator, getFieldError} = this.props.form;

        return (<React.Fragment>
            <Grid item xs={12}>
                <Paper className="root" style={{padding: 14}} elevation={0}>
                    <FormControl
                        fullWidth={true}
                        required={true}
                        style={{marginBottom: 30, marginTop: 20}}
                        className="with-select"
                    >
                        <InputLabel id="messages-receivers-label">Receivers</InputLabel>

                        {getFieldDecorator("receiver_type",
                            {
                                rules: [{
                                    required: true,
                                    message: "Please select receiver type",
                                }]
                            },
                        )(
                            <Select
                                labelId="messages-receivers-label"
                                id="messagesReceivers"
                                size="small"
                                label="Receivers"
                            >
                                <MenuItem value={"global"}>Global</MenuItem>
                                <MenuItem value={"vip_users"}>
                                    Users with active VIP <b>({this.state.vipPlayers === null ? "Fetching..." : this.state.vipPlayers})</b>
                                </MenuItem>
                                <MenuItem value={"users"}>Specific Users</MenuItem>
                                <MenuItem value={"squads"}>Specific Alliances</MenuItem>
                            </Select>
                        )}

                    </FormControl>

                    {this.renderStoreInput()}
                    {this.renderReceiverWarning()}
                    {this.props.form.getFieldValue("receiver_type") !== "vip_users" && this.renderReceiverInput()}

                    <FormControl
                        fullWidth={true}
                        required={true}
                        style={{marginBottom: 30, marginTop: 20}}
                        error={getFieldError("title")}
                    >
                        {getFieldDecorator("title",
                            {
                                validateTrigger: "onBlur",
                                rules: [{
                                    required: true,
                                    message: "Please enter title",
                                }]
                            },
                        )(
                            <TextField
                                label="Title"
                                id="message-title-input"
                                size="small"
                            />
                        )}

                        {getFieldError("title") ?
                            <FormHelperText>{getFieldError("title").join(",")}</FormHelperText>
                            : null}
                    </FormControl>

                    <FormControl
                        fullWidth={true}
                        required={true}
                        style={{marginBottom: 30}}
                        error={getFieldError("body")}
                    >
                        {getFieldDecorator("body",
                            {
                                validateTrigger: "onBlur",
                                rules: [{
                                    required: true,
                                    message: "Please enter body",
                                }]
                            },
                        )(
                            <TextField
                                label="Body"
                                multiline={true}
                                rows="10"
                                id="message-body-input"
                                size="small"
                            />
                        )}

                        {getFieldError("body") ?
                            <FormHelperText>{getFieldError("body").join(",")}</FormHelperText>
                            : null}
                    </FormControl>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper className="root" style={{padding: 14}} elevation={0}>
                    <Typography variant="h6" gutterBottom>
                        Custom rewards
                    </Typography>

                    <div style={{marginBottom: 20}}>
                        {this.renderMessageRewards()}
                    </div>

                    <Button disableElevation onClick={this.addNewItem} style={{width: "100%"}} color="primary" variant="contained" size="small">
                        <AddIcon/> Add item
                    </Button>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper className="root" style={{padding: 14}} elevation={0}>
                    <Typography variant="h6" gutterBottom>
                        Image attachments
                    </Typography>

                    <div style={{marginBottom: 20}}>
                        <table>
                            <tbody>
                                {this.renderImages()}
                            </tbody>
                        </table>
                    </div>

                    <input
                        type="file"
                        accept="image/jpeg, image/png"
                        onChange={this.uploadFile}
                    />
                </Paper>
            </Grid>

            <Grid item xs={12} style={{ textAlign: "right", marginTop: 20 }}>
                <Button disableElevation onClick={() => this.createMessageTask()} variant="contained" color="primary">Create task</Button>
            </Grid>
        </React.Fragment>);
    }
}
